import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarExport,
} from '@mui/x-data-grid';
import {
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  CalendarToday as CalendarTodayIcon,
  Print as PrintIcon,
  MoneyOff as RefundIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Draw as DrawIcon,
  Payment as PaymentIcon,
  LocalAtm as CashIcon,
  AccountBalance as CheckIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Order, OrderItem, PaymentStatus, OrderUserType, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';
import { CreateManualOrderDialog } from './CreateManualOrderDialog';

interface OrdersManagementProps {
  permissions: CrudPermissions;
}

const darkButtonSx = {
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  },
};

const statusColors: Record<PaymentStatus, 'success' | 'warning' | 'error' | 'default'> = {
  completed: 'success',
  pending: 'warning',
  failed: 'error',
  refunded: 'default',
};

const userTypeColors: Record<OrderUserType, 'primary' | 'secondary' | 'info' | 'default'> = {
  student: 'primary',
  parent: 'secondary',
  community: 'info',
  guest: 'default',
};

interface OrderFilters {
  startDate: Date | null;
  endDate: Date | null;
  paymentStatus: PaymentStatus | 'all';
  userType: OrderUserType | 'all';
  searchQuery: string;
}

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

export function OrdersManagement({ permissions }: OrdersManagementProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { orders, orderItems, students, addOrder, setOrderItems } = useData();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [manualPaymentOpen, setManualPaymentOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'check'>('cash');
  const [checkNumber, setCheckNumber] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [filters, setFilters] = useState<OrderFilters>({
    startDate: null,
    endDate: null,
    paymentStatus: 'all',
    userType: 'all',
    searchQuery: '',
  });

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (filters.startDate && new Date(order.createdAt) < filters.startDate) {
        return false;
      }
      if (filters.endDate) {
        const endOfDay = new Date(filters.endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (new Date(order.createdAt) > endOfDay) {
          return false;
        }
      }
      if (filters.paymentStatus !== 'all' && order.paymentStatus !== filters.paymentStatus) {
        return false;
      }
      if (filters.userType !== 'all' && order.userType !== filters.userType) {
        return false;
      }
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        return (
          order.orderNumber.toLowerCase().includes(query) ||
          order.userName.toLowerCase().includes(query) ||
          order.userEmail.toLowerCase().includes(query) ||
          order.transactionId?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [orders, filters]);

  const statistics = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const todayOrders = filteredOrders.filter(
      (o) => new Date(o.createdAt) >= todayStart && o.paymentStatus === 'completed'
    );
    const weekOrders = filteredOrders.filter(
      (o) => new Date(o.createdAt) >= weekStart && o.paymentStatus === 'completed'
    );
    const monthOrders = filteredOrders.filter(
      (o) => new Date(o.createdAt) >= monthStart && o.paymentStatus === 'completed'
    );

    return {
      today: {
        count: todayOrders.length,
        total: todayOrders.reduce((sum, o) => sum + o.total, 0),
      },
      week: {
        count: weekOrders.length,
        total: weekOrders.reduce((sum, o) => sum + o.total, 0),
      },
      month: {
        count: monthOrders.length,
        total: monthOrders.reduce((sum, o) => sum + o.total, 0),
      },
      all: {
        count: filteredOrders.filter((o) => o.paymentStatus === 'completed').length,
        total: filteredOrders
          .filter((o) => o.paymentStatus === 'completed')
          .reduce((sum, o) => sum + o.total, 0),
      },
    };
  }, [filteredOrders]);

  const handleViewOrder = (order: Order) => {
    const items = orderItems.filter((item) => item.orderId === order.id);
    setSelectedOrder({ ...order, items });
    setOrderDetailsOpen(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsOpen(false);
    setSelectedOrder(null);
  };

  const handleOpenManualPayment = () => {
    if (!selectedOrder) return;
    setPaymentAmount(selectedOrder.total.toFixed(2));
    setPaymentMethod('cash');
    setCheckNumber('');
    setPaymentNotes('');
    setManualPaymentOpen(true);
  };

  const handleCloseManualPayment = () => {
    setManualPaymentOpen(false);
    setPaymentAmount('');
    setCheckNumber('');
    setPaymentNotes('');
  };

  const handleOrderCreated = (order: any) => {
    addOrder(order);

    if (order.items && order.items.length > 0) {
      setOrderItems((prev) => [...prev, ...order.items]);
    }

    setCreateOrderOpen(false);
  };

  const handleProcessManualPayment = () => {
    if (!selectedOrder) return;

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    if (paymentMethod === 'check' && !checkNumber.trim()) {
      alert('Please enter a check number');
      return;
    }

    const paymentMethodLabel = paymentMethod === 'cash' ? 'Cash' : `Check #${checkNumber}`;
    const transactionId = paymentMethod === 'check' ? `CHECK-${checkNumber}` : `CASH-${Date.now()}`;

    let notes = selectedOrder.notes || '';
    const paymentNote = `Manual payment recorded: ${paymentMethodLabel} - $${amount.toFixed(2)} on ${new Date().toLocaleString()}`;
    notes = notes ? `${notes}\n\n${paymentNote}` : paymentNote;

    if (paymentNotes.trim()) {
      notes += `\nPayment Notes: ${paymentNotes}`;
    }

    const updatedOrder = {
      ...selectedOrder,
      paymentStatus: 'completed' as PaymentStatus,
      paymentMethod: paymentMethodLabel,
      transactionId,
      notes,
      completedAt: new Date(),
    };

    setSelectedOrder(updatedOrder);
    handleCloseManualPayment();
  };

  const getStudentInfo = (studentId?: string) => {
    if (!studentId) return null;
    return students.find((s) => s.id === studentId);
  };

  const handlePrintReceipt = () => {
    if (!selectedOrder) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const studentInfo = getStudentInfo(selectedOrder.studentId);
    const itemsHtml = selectedOrder.items
      ?.map(
        (item) => `
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${item.feeName}</td>
            <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.quantity}</td>
            <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right;">$${item.unitPrice.toFixed(2)}</td>
            <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right;">$${item.totalPrice.toFixed(2)}</td>
          </tr>
        `
      )
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${selectedOrder.orderNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #1e293b; padding-bottom: 20px; }
            .header h1 { margin: 0; color: #1e293b; }
            .header p { color: #64748b; margin: 5px 0 0; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
            .info-section h3 { margin: 0 0 8px; color: #374151; font-size: 14px; text-transform: uppercase; }
            .info-section p { margin: 0; color: #1f2937; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th { background: #f3f4f6; padding: 10px 8px; text-align: left; font-weight: 600; }
            th:nth-child(2), th:nth-child(3), th:nth-child(4) { text-align: center; }
            th:nth-child(3), th:nth-child(4) { text-align: right; }
            .totals { margin-left: auto; width: 250px; }
            .totals-row { display: flex; justify-content: space-between; padding: 8px 0; }
            .totals-row.total { font-weight: bold; font-size: 18px; border-top: 2px solid #1e293b; margin-top: 8px; padding-top: 16px; }
            .status { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .status-completed { background: #dcfce7; color: #166534; }
            .status-pending { background: #fef3c7; color: #92400e; }
            .status-refunded { background: #e5e7eb; color: #374151; }
            .status-failed { background: #fee2e2; color: #991b1b; }
            .footer { margin-top: 40px; text-align: center; color: #64748b; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Receipt</h1>
            <p>Order #${selectedOrder.orderNumber}</p>
          </div>

          <div class="info-grid">
            <div class="info-section">
              <h3>Customer Information</h3>
              <p><strong>${selectedOrder.userName}</strong></p>
              <p>${selectedOrder.userEmail}</p>
              <p style="margin-top: 4px; text-transform: capitalize;">${selectedOrder.userType}</p>
            </div>
            <div class="info-section">
              <h3>Order Information</h3>
              <p><strong>Date:</strong> ${new Date(selectedOrder.createdAt).toLocaleString()}</p>
              <p><strong>Payment:</strong> ${selectedOrder.paymentMethod || 'N/A'}</p>
              <p><strong>Status:</strong> <span class="status status-${selectedOrder.paymentStatus}">${selectedOrder.paymentStatus}</span></p>
              ${selectedOrder.transactionId ? `<p><strong>Transaction:</strong> ${selectedOrder.transactionId}</p>` : ''}
            </div>
            ${
              studentInfo
                ? `
            <div class="info-section">
              <h3>Student Information</h3>
              <p><strong>${studentInfo.firstName} ${studentInfo.lastName}</strong></p>
              <p>ID: ${studentInfo.studentIdNumber}</p>
              <p>Grade: ${studentInfo.grade}</p>
            </div>
            `
                : ''
            }
          </div>

          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>

          <div class="totals">
            <div class="totals-row">
              <span>Subtotal:</span>
              <span>$${selectedOrder.subtotal.toFixed(2)}</span>
            </div>
            <div class="totals-row">
              <span>Tax:</span>
              <span>$${selectedOrder.tax.toFixed(2)}</span>
            </div>
            <div class="totals-row total">
              <span>Total:</span>
              <span>$${selectedOrder.total.toFixed(2)}</span>
            </div>
          </div>

          ${selectedOrder.notes ? `<div style="margin-top: 30px; padding: 16px; background: #f9fafb; border-radius: 8px;"><strong>Notes:</strong><br/>${selectedOrder.notes}</div>` : ''}

          <div class="footer">
            <p>Thank you for your purchase!</p>
            <p>Printed on ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getStatusIcon = (status: PaymentStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'pending':
        return <WarningIcon sx={{ color: 'warning.main' }} />;
      case 'failed':
        return <CloseIcon sx={{ color: 'error.main' }} />;
      case 'refunded':
        return <RefundIcon sx={{ color: 'text.secondary' }} />;
      default:
        return <InfoIcon />;
    }
  };

  const handleExportCSV = () => {
    const headers = [
      'Order Number',
      'Date',
      'Customer Name',
      'Customer Email',
      'User Type',
      'Payment Status',
      'Payment Method',
      'Transaction ID',
      'Subtotal',
      'Tax',
      'Total',
    ];

    const rows = filteredOrders.map(order => [
      order.orderNumber,
      new Date(order.createdAt).toLocaleString(),
      order.userName,
      order.userEmail,
      order.userType,
      order.paymentStatus,
      order.paymentMethod || 'N/A',
      order.transactionId || '-',
      order.subtotal.toFixed(2),
      order.tax.toFixed(2),
      order.total.toFixed(2),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `orders_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: GridColDef[] = [
    {
      field: 'orderNumber',
      headerName: 'Order #',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight="bold">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 1,
      minWidth: 160,
      valueFormatter: (value) => {
        return new Date(value).toLocaleString();
      },
    },
    {
      field: 'userName',
      headerName: 'Customer',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'userType',
      headerName: 'Type',
      flex: 0.6,
      minWidth: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={userTypeColors[params.value as OrderUserType]}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'total',
      headerName: 'Total',
      flex: 0.6,
      minWidth: 100,
      valueFormatter: (value) => {
        return `$${Number(value).toFixed(2)}`;
      },
    },
    {
      field: 'paymentStatus',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={statusColors[params.value as PaymentStatus]}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'paymentMethod',
      headerName: 'Payment Method',
      flex: 0.8,
      minWidth: 130,
      valueFormatter: (value) => {
        return value || 'N/A';
      },
    },
    {
      field: 'transactionId',
      headerName: 'Transaction ID',
      flex: 0.8,
      minWidth: 130,
      valueFormatter: (value) => {
        return value || '-';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewOrder(params.row as Order)}
            color="primary"
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const renderMobileCards = () => (
    <Stack spacing={2}>
      {filteredOrders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No orders found</Typography>
        </Paper>
      ) : (
        filteredOrders.map((order) => (
          <Card key={order.id} sx={{ width: '100%' }}>
            <CardContent sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {order.orderNumber}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(order.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  ${order.total.toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                    {order.userName}
                  </Typography>
                  <Chip
                    label={order.userType}
                    size="small"
                    color={userTypeColors[order.userType]}
                    sx={{ textTransform: 'capitalize', flexShrink: 0 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {order.userEmail}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {order.paymentMethod || 'N/A'}
                  </Typography>
                  <Box sx={{ ml: 'auto' }}>
                    <Chip
                      label={order.paymentStatus}
                      size="small"
                      color={statusColors[order.paymentStatus]}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                </Box>

                {order.transactionId && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
                    Trans: {order.transactionId}
                  </Typography>
                )}
              </Stack>
            </CardContent>

            <Divider />

            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => handleViewOrder(order)}
                sx={{
                  borderColor: '#1e293b',
                  color: '#1e293b',
                  '&:hover': {
                    borderColor: '#0f172a',
                    backgroundColor: 'rgba(15, 23, 42, 0.04)',
                  },
                }}
              >
                View Details
              </Button>
            </Box>
          </Card>
        ))
      )}
    </Stack>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h5" fontWeight={600}>Orders Management</Typography>
          </Box>
        </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: { xs: 2, sm: 3 },
          mb: 3,
        }}
      >
        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <AttachMoneyIcon sx={{ color: 'success.main', mr: 1, fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Today
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mb: 0.25, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              ${statistics.today.total.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {statistics.today.count} orders
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <TrendingUpIcon sx={{ color: 'info.main', mr: 1, fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                7 Days
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mb: 0.25, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              ${statistics.week.total.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {statistics.week.count} orders
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <ShoppingCartIcon sx={{ color: 'primary.main', mr: 1, fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Month
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mb: 0.25, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              ${statistics.month.total.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {statistics.month.count} orders
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <ReceiptIcon sx={{ color: 'secondary.main', mr: 1, fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                All Time
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mb: 0.25, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              ${statistics.all.total.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {statistics.all.count} orders
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
            mb: 2,
          }}>
            <Typography variant="h6">
              Filters
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOrderOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
                  },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Create Manual Order
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                disabled={filteredOrders.length === 0}
                sx={{
                  ...darkButtonSx,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export CSV ({filteredOrders.length})
              </Button>
            </Box>
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
            gap: 2,
          }}>
            <DatePicker
              label="Start Date"
              value={filters.startDate}
              onChange={(date) => setFilters({ ...filters, startDate: date })}
              slotProps={{
                textField: { fullWidth: true, size: 'small' },
                actionBar: { actions: ['clear', 'today'] },
              }}
            />
            <DatePicker
              label="End Date"
              value={filters.endDate}
              onChange={(date) => setFilters({ ...filters, endDate: date })}
              slotProps={{
                textField: { fullWidth: true, size: 'small' },
                actionBar: { actions: ['clear', 'today'] },
              }}
            />
            <TextField
              select
              fullWidth
              size="small"
              label="Payment Status"
              value={filters.paymentStatus}
              onChange={(e) =>
                setFilters({ ...filters, paymentStatus: e.target.value as PaymentStatus | 'all' })
              }
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="User Type"
              value={filters.userType}
              onChange={(e) =>
                setFilters({ ...filters, userType: e.target.value as OrderUserType | 'all' })
              }
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="student">Student</MenuItem>
              <MenuItem value="parent">Parent</MenuItem>
              <MenuItem value="community">Community</MenuItem>
              <MenuItem value="guest">Guest</MenuItem>
            </TextField>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Order #, Name, Email..."
              value={filters.searchQuery}
              onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
            />
          </Box>
        </CardContent>
      </Card>

      {isMobile ? (
        renderMobileCards()
      ) : (
        <Paper data-tour="admin-orders-table" sx={{ width: '100%', overflow: 'hidden' }}>
          <DataGrid
            rows={filteredOrders}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{ toolbar: CustomToolbar }}
            autoHeight
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-main': {
                width: '100%',
              },
            }}
          />
        </Paper>
      )}

      <Dialog
        open={orderDetailsOpen}
        onClose={handleCloseOrderDetails}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ReceiptIcon sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" component="span">
                Order {selectedOrder?.orderNumber}
              </Typography>
              {selectedOrder && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {getStatusIcon(selectedOrder.paymentStatus)}
                  <Chip
                    label={selectedOrder.paymentStatus}
                    size="small"
                    color={statusColors[selectedOrder.paymentStatus]}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <IconButton onClick={handleCloseOrderDetails} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedOrder && (
            <Stack spacing={3}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Customer Information
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedOrder.userName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selectedOrder.userEmail}
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <Chip
                      label={selectedOrder.userType}
                      size="small"
                      color={userTypeColors[selectedOrder.userType]}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                </Paper>

                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CalendarTodayIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Order Details
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Order Date
                      </Typography>
                      <Typography variant="body2">
                        {new Date(selectedOrder.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                    {selectedOrder.completedAt && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Completed Date
                        </Typography>
                        <Typography variant="body2">
                          {new Date(selectedOrder.completedAt).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CreditCardIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Payment Information
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Payment Method
                      </Typography>
                      <Typography variant="body2">
                        {selectedOrder.paymentMethod || 'N/A'}
                      </Typography>
                    </Box>
                    {selectedOrder.transactionId && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Transaction ID
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            bgcolor: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            display: 'inline-block',
                          }}
                        >
                          {selectedOrder.transactionId}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>

                {selectedOrder.studentId && getStudentInfo(selectedOrder.studentId) && (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <SchoolIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Student Information
                      </Typography>
                    </Box>
                    {(() => {
                      const student = getStudentInfo(selectedOrder.studentId);
                      if (!student) return null;
                      return (
                        <Stack spacing={1}>
                          <Typography variant="body1" fontWeight={500}>
                            {student.firstName} {student.lastName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Student ID
                              </Typography>
                              <Typography variant="body2">{student.studentIdNumber}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Grade
                              </Typography>
                              <Typography variant="body2">{student.grade}</Typography>
                            </Box>
                          </Box>
                        </Stack>
                      );
                    })()}
                  </Paper>
                )}
              </Box>

              {selectedOrder.notes && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'info.lighter',
                    border: 1,
                    borderColor: 'info.light',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Notes
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {selectedOrder.notes}
                  </Typography>
                </Paper>
              )}

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ShoppingCartIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="h6">
                    Order Items ({selectedOrder.items?.length || 0})
                  </Typography>
                </Box>

                {(!selectedOrder.items || selectedOrder.items.length === 0) ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                    }}
                  >
                    <Typography color="text.secondary">No items found for this order</Typography>
                  </Paper>
                ) : (
                  selectedOrder.items.map((item: OrderItem) => (
                    <Paper
                      key={item.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {item.feeName}
                          </Typography>
                          <Chip
                            label={item.feeCategory}
                            size="small"
                            sx={{ mt: 0.5, bgcolor: 'grey.100' }}
                          />
                        </Box>
                        <Typography variant="h6" color="primary.main" fontWeight={700}>
                          ${item.totalPrice.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                          gap: 2,
                          py: 1.5,
                          borderTop: 1,
                          borderBottom: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Quantity
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {item.quantity}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Unit Price
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            ${item.unitPrice.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Pricing Tier
                          </Typography>
                          <Chip
                            label={item.pricingTier}
                            size="small"
                            sx={{
                              textTransform: 'capitalize',
                              bgcolor:
                                item.pricingTier === 'full'
                                  ? '#e0f2fe'
                                  : item.pricingTier === 'reduced'
                                  ? '#fef3c7'
                                  : '#d1fae5',
                              color:
                                item.pricingTier === 'full'
                                  ? '#0369a1'
                                  : item.pricingTier === 'reduced'
                                  ? '#b45309'
                                  : '#047857',
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Purchase Date
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {new Date(item.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>

                      {item.partialPaymentAmount && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                          <Typography variant="body2" color="info.main" fontWeight={500}>
                            Partial Payment Applied: ${item.partialPaymentAmount.toFixed(2)}
                          </Typography>
                        </Box>
                      )}

                      {item.questionnaireResponses &&
                        Object.keys(item.questionnaireResponses).length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <AssignmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                              <Typography variant="body2" fontWeight={600}>
                                Questionnaire Responses
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                pl: 2,
                                borderLeft: 3,
                                borderColor: 'primary.light',
                              }}
                            >
                              {Object.entries(item.questionnaireResponses).map(([key, value]) => (
                                <Box key={key} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {key}
                                  </Typography>
                                  <Typography variant="body2">
                                    {Array.isArray(value) ? value.join(', ') : String(value)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                      {item.signatureData && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: 'success.lighter',
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'success.light',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              Waiver Signed
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Signed by <strong>{item.signatureData.signedBy}</strong> on{' '}
                            {new Date(item.signatureData.signedAt).toLocaleString()}
                          </Typography>
                          {item.signatureData.drawnSignature && (
                            <Box sx={{ mt: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <DrawIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  Signature
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  bgcolor: 'white',
                                  border: 1,
                                  borderColor: 'grey.300',
                                  borderRadius: 1,
                                  p: 1,
                                  display: 'inline-block',
                                }}
                              >
                                <img
                                  src={item.signatureData.drawnSignature}
                                  alt="Signature"
                                  style={{ maxWidth: 200, maxHeight: 60 }}
                                />
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Paper>
                  ))
                )}
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: '#f8fafc',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                  Order Summary
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography variant="body2">${selectedOrder.subtotal.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Tax
                    </Typography>
                    <Typography variant="body2">${selectedOrder.tax.toFixed(2)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6" color="primary.main" fontWeight={700}>
                      ${selectedOrder.total.toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintReceipt}>
            Print Receipt
          </Button>
          {selectedOrder && selectedOrder.paymentStatus !== 'completed' && permissions.update && (
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={handleOpenManualPayment}
              sx={{
                bgcolor: '#22c55e',
                '&:hover': { bgcolor: '#16a34a' },
              }}
            >
              Record Manual Payment
            </Button>
          )}
          <Button variant="contained" onClick={handleCloseOrderDetails} sx={darkButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manualPaymentOpen}
        onClose={handleCloseManualPayment}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PaymentIcon sx={{ color: 'success.main' }} />
            <Box>
              <Typography variant="h6">Record Manual Payment</Typography>
              <Typography variant="caption" color="text.secondary">
                Order {selectedOrder?.orderNumber}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Order Total
              </Typography>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                ${selectedOrder?.total.toFixed(2)}
              </Typography>
            </Paper>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Payment Method
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Paper
                  onClick={() => setPaymentMethod('cash')}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: paymentMethod === 'cash' ? 'success.main' : 'divider',
                    bgcolor: paymentMethod === 'cash' ? 'success.50' : 'white',
                    borderRadius: 2,
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'success.main',
                      bgcolor: 'success.50',
                    },
                  }}
                >
                  <CashIcon
                    sx={{
                      fontSize: 40,
                      color: paymentMethod === 'cash' ? 'success.main' : 'text.secondary',
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="body1"
                    fontWeight={paymentMethod === 'cash' ? 600 : 400}
                    color={paymentMethod === 'cash' ? 'success.main' : 'text.primary'}
                  >
                    Cash
                  </Typography>
                </Paper>
                <Paper
                  onClick={() => setPaymentMethod('check')}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: paymentMethod === 'check' ? 'primary.main' : 'divider',
                    bgcolor: paymentMethod === 'check' ? 'primary.50' : 'white',
                    borderRadius: 2,
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50',
                    },
                  }}
                >
                  <CheckIcon
                    sx={{
                      fontSize: 40,
                      color: paymentMethod === 'check' ? 'primary.main' : 'text.secondary',
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="body1"
                    fontWeight={paymentMethod === 'check' ? 600 : 400}
                    color={paymentMethod === 'check' ? 'primary.main' : 'text.primary'}
                  >
                    Check
                  </Typography>
                </Paper>
              </Box>
            </Box>

            {paymentMethod === 'check' && (
              <TextField
                fullWidth
                label="Check Number"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                placeholder="Enter check number"
                required
                helperText="Required for check payments"
              />
            )}

            <TextField
              fullWidth
              label="Payment Amount"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>,
              }}
              helperText="Enter the amount received"
            />

            <TextField
              fullWidth
              label="Payment Notes (Optional)"
              multiline
              rows={3}
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              placeholder="Add any additional notes about this payment..."
            />

            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'info.lighter',
                border: 1,
                borderColor: 'info.light',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <InfoIcon sx={{ color: 'info.main', fontSize: 20, mt: 0.25 }} />
                <Typography variant="body2" color="info.dark">
                  This will mark the order as completed and record the payment details. The payment
                  information will be saved in the order notes for tracking purposes.
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleCloseManualPayment}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={handleProcessManualPayment}
            disabled={
              !paymentAmount ||
              parseFloat(paymentAmount) <= 0 ||
              (paymentMethod === 'check' && !checkNumber.trim())
            }
            sx={{
              bgcolor: '#22c55e',
              '&:hover': { bgcolor: '#16a34a' },
            }}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      <CreateManualOrderDialog
        open={createOrderOpen}
        onClose={() => setCreateOrderOpen(false)}
        onOrderCreated={handleOrderCreated}
      />
      </Box>
    </LocalizationProvider>
  );
}
